import { useState, useCallback } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { messageApi } from '../../../services/api.service';
import type { Message } from '../../../types/chat.types';
import { type ChannelCapabilities, getChannelCapabilities } from '../channels/types';

export type { ChannelCapabilities };
export { getChannelCapabilities };

/**
 * Check if a message can be edited based on time limit.
 */
export function canEditMessage(
  message: Message,
  isOwnMessage: boolean,
  capabilities: ChannelCapabilities
): boolean {
  if (!isOwnMessage) return false;
  if (!capabilities.canEdit) return false;
  if (message.isDeleted) return false;
  if (message.type !== 'text') return false;

  if (capabilities.editTimeLimit === null) return true;

  const messageAge = Date.now() - new Date(message.createdAt).getTime();
  return messageAge <= capabilities.editTimeLimit;
}

/**
 * Check if a message can be deleted based on time limit.
 */
export function canDeleteMessage(
  message: Message,
  isOwnMessage: boolean,
  capabilities: ChannelCapabilities
): boolean {
  if (!isOwnMessage) return false;
  if (!capabilities.canDelete) return false;
  if (message.isDeleted) return false;

  if (capabilities.deleteTimeLimit === null) return true;

  const messageAge = Date.now() - new Date(message.createdAt).getTime();
  return messageAge <= capabilities.deleteTimeLimit;
}

/**
 * Get remaining time for an action (edit/delete) in a human-readable format.
 * @param timeLimitMs Time limit in milliseconds (null = no limit)
 */
export function getRemainingTime(
  message: Message,
  timeLimitMs: number | null
): string | null {
  if (timeLimitMs === null) return null;

  const messageAge = Date.now() - new Date(message.createdAt).getTime();
  const remainingMs = timeLimitMs - messageAge;

  if (remainingMs <= 0) return null;

  const remainingMinutes = Math.ceil(remainingMs / 60000);
  if (remainingMinutes > 1) {
    return `${remainingMinutes} min`;
  }
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  return `${remainingSeconds}s`;
}

interface UseMessageActionsResult {
  /** Edit a message */
  editMessage: (messageId: number, newBody: string) => Promise<boolean>;
  /** Delete a message */
  deleteMessage: (messageId: number, hardDelete?: boolean) => Promise<boolean>;
  /** React to a message */
  reactToMessage: (messageId: number, emoji: string) => Promise<boolean>;
  /** Remove reaction from a message */
  removeReaction: (messageId: number) => Promise<boolean>;
  /** Whether an action is in progress */
  isLoading: boolean;
  /** Current action being performed */
  loadingAction: 'edit' | 'delete' | 'react' | null;
}

/**
 * Hook for message actions (edit, delete, react).
 */
export function useMessageActions(): UseMessageActionsResult {
  const notify = useNotify();
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<'edit' | 'delete' | 'react' | null>(null);

  const editMessage = useCallback(
    async (messageId: number, newBody: string): Promise<boolean> => {
      setIsLoading(true);
      setLoadingAction('edit');
      try {
        await messageApi.edit(messageId, newBody);
        notify(translate('resources.message.notifications.editSuccess'), {
          type: 'success',
        });
        return true;
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : translate('ra.message.error');
        notify(message, { type: 'error' });
        return false;
      } finally {
        setIsLoading(false);
        setLoadingAction(null);
      }
    },
    [notify, translate]
  );

  const deleteMessage = useCallback(
    async (messageId: number, hardDelete = false): Promise<boolean> => {
      setIsLoading(true);
      setLoadingAction('delete');
      try {
        await messageApi.delete(messageId, hardDelete);
        notify(
          translate('resources.message.notifications.deleteSuccess'),
          { type: 'success' }
        );
        return true;
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : translate('ra.message.error');
        notify(message, { type: 'error' });
        return false;
      } finally {
        setIsLoading(false);
        setLoadingAction(null);
      }
    },
    [notify, translate]
  );

  const reactToMessage = useCallback(
    async (messageId: number, emoji: string): Promise<boolean> => {
      setIsLoading(true);
      setLoadingAction('react');
      try {
        await messageApi.react(messageId, { emoji });
        return true;
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : translate('ra.message.error');
        notify(message, { type: 'error' });
        return false;
      } finally {
        setIsLoading(false);
        setLoadingAction(null);
      }
    },
    [notify, translate]
  );

  const removeReaction = useCallback(
    async (messageId: number): Promise<boolean> => {
      setIsLoading(true);
      setLoadingAction('react');
      try {
        await messageApi.removeReaction(messageId);
        return true;
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : translate('ra.message.error');
        notify(message, { type: 'error' });
        return false;
      } finally {
        setIsLoading(false);
        setLoadingAction(null);
      }
    },
    [notify, translate]
  );

  return {
    editMessage,
    deleteMessage,
    reactToMessage,
    removeReaction,
    isLoading,
    loadingAction,
  };
}
